import React from "react";
import type { ConfigProviderProps, RadioChangeEvent } from "antd";
import { Col, Collapse, ConfigProvider, Radio, Row, Tabs } from "antd";
import { SettingMasterdataEnum } from "src/entity/common/menus-enum.enum";
import CombustionTable from "./components/CombustionTable";
import { useDidMount } from "src/hook/useDidMount";
import { settingStore } from "src/stores/setting-store";
import { useShallow } from "zustand/react/shallow";
import { componentPalette } from "src/theme/component-palette";
import FlareInformationTable from "./components/FlareInformationTable";
import FlareCompound from "./components/FlareCompound";
import "./components/CustomCollapse.css";
import { utilsStore } from "src/stores/utils-store";
import ChemicalProperty from "./components/TerminalMarketing/ChemicalPropertyTable";
import LoadingMethod from "./components/TerminalMarketing/LoadingMethodTable";
import EmissionControl from "./components/TerminalMarketing/EmissionControlTable";
import TankType from "./components/Tank/TankTypeTable";
import TankEmissionControl from "./components/Tank/TankEmissionControlTable";
import WasteWaterTable from "./components/WasteWaterTable";
import Fugitive from "src/pages/VocsInventory/Fugitive";
import FugitiveStatusTable from "./components/Fugitive/FugitiveStatusTable";
import FugitiveEqIdTable from "./components/Fugitive/FugitiveEqTypeTable";
import FugitiveUncheckTable from "./components/Fugitive/FugitiveUncheckTable";
import FuitiveSTDControlTable from "./components/Fugitive/FugitiveSTDControlTable";
import FugitiveComBySeriesTable from "./components/Fugitive/FugitiveComBySeriesTable";

const SettingCalculation: React.FC = () => {
  const renderContent = (menu: string) => {
    if (menu === SettingMasterdataEnum.COMBUSTION) {
      return <CombustionTable />;
    } else if (menu === SettingMasterdataEnum.FLARE_INFORMATION) {
      return <FlareInformationTable />;
    } else if (menu === SettingMasterdataEnum.FLARE_COMPOUND) {
      return <FlareCompound />;
    } else if (menu === SettingMasterdataEnum.TRANSPORTATION_AND_MARKETING_CHEMICAL) {
      return <ChemicalProperty />;
    } else if (menu === SettingMasterdataEnum.TRANSPORTATION_AND_MARKETING_LOADING_METHOD) {
      return <LoadingMethod />;
    } else if (menu === SettingMasterdataEnum.TRANSPORTATION_AND_MARKETING_EMISSION_CONTROL) {
      return <EmissionControl />;
    } else if (menu === SettingMasterdataEnum.TANK_TANK_TYPE) {
      return <TankType />;
    } else if (menu === SettingMasterdataEnum.TANK_EMISSION_CONTROL) {
      return <TankEmissionControl />;
    } else if (menu === SettingMasterdataEnum.WASTE_WATER_TREATMENT) {
      return <WasteWaterTable />;
    } else if (menu === SettingMasterdataEnum.FUGITIVE_STATUS) {
      return <FugitiveStatusTable />;
    } else if (menu === SettingMasterdataEnum.FUGITIVE_EQUIPMENT_TYPE) {
      return <FugitiveEqIdTable />;
    } else if (menu === SettingMasterdataEnum.FUGITIVE_UNCHECK) {
      return <FugitiveUncheckTable />;
    } else if (menu === SettingMasterdataEnum.FUGITIVE_STD_CONTROL) {
      return <FuitiveSTDControlTable />;
    } else if (menu === SettingMasterdataEnum.FUGITIVE_COMPONENT_BY_SERIES) {
      return <FugitiveComBySeriesTable />;
    }

    return <Row className="bg-white w-full">{menu}</Row>;
  };

  const getFilterCombustionColumn = settingStore(useShallow((state) => state.getFilterCombustionColumn));
  const getFuelType = settingStore(useShallow((state) => state.getFuelType));
  const getEmission = settingStore(useShallow((state) => state.getEmission));
  const getUnit = settingStore(useShallow((state) => state.getUnit));
  const setActiveMasterdata = utilsStore(useShallow((state) => state.setActiveMasterdataTab));

  useDidMount(() => {
    getFuelType({ type: "combustion", label: "Fuel Type" });
    getEmission({ type: "global", label: "Emission" });
    getUnit({ type: "combustion", label: "unit" });
    getFilterCombustionColumn("remark");
  });

  const titleBar = [
    {
      key: "1",
      label: "Combustion",
      menuValue: SettingMasterdataEnum.COMBUSTION,
      children: (
        <Col span={24} className="bg-white pt-4 pb-4 rounded-b-md -mt-4">
          <Collapse
            className="bg-white"
            size="large"
            items={[
              {
                key: "1",
                label: "Emission Factor",
                children: renderContent(SettingMasterdataEnum.COMBUSTION),
              },
            ]}
          />
        </Col>
      ),
    },
    {
      key: "2",
      label: "Flare",
      menuValue: SettingMasterdataEnum.FLARE,
      children: (
        <Col span={24} className="bg-white pt-4 pb-4 rounded-b-md -mt-4">
          <Collapse
            className="bg-white"
            size="large"
            items={[
              { key: "1", label: "Flare Information", children: renderContent(SettingMasterdataEnum.FLARE_INFORMATION) },
              { key: "2", label: "Compound", children: renderContent(SettingMasterdataEnum.FLARE_COMPOUND) },
            ]}
          />
        </Col>
      ),
    },
    {
      key: "3",
      label: "Transportation & Marketing",
      menuValue: SettingMasterdataEnum.TRANSPORTATION_AND_MARKETING_CHEMICAL,
      children: (
        <div className="bg-white pt-4 pb-4 rounded-b-md -mt-4">
          <Col span={24} className="pt-4 pb-4 rounded-b-md -mt-4">
            <Collapse
              className="bg-white"
              size="large"
              items={[
                {
                  key: "1",
                  label: "Chemical Property",
                  children: renderContent(SettingMasterdataEnum.TRANSPORTATION_AND_MARKETING_CHEMICAL),
                },
              ]}
            />
          </Col>
          <Row gutter={[16, 16]}>
            <Col className="pt-4 pb-4 rounded-b-md -mt-4" span={12}>
              <Collapse
                className="bg-white"
                size="large"
                items={[
                  {
                    key: "2",
                    label: "Loading Method",
                    children: renderContent(SettingMasterdataEnum.TRANSPORTATION_AND_MARKETING_LOADING_METHOD),
                  },
                ]}
              />
            </Col>
            <Col className="pt-4 pb-4 rounded-b-md -mt-4" span={12}>
              <Collapse
                className="bg-white"
                size="large"
                items={[
                  {
                    key: "3",
                    label: "Emission Control",
                    children: renderContent(SettingMasterdataEnum.TRANSPORTATION_AND_MARKETING_EMISSION_CONTROL),
                  },
                ]}
              />
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: "4",
      label: "Fugitive",
      menuValue: SettingMasterdataEnum.FUGITIVE,
      children: (
        <div className="bg-white pt-4 pb-4 rounded-b-md -mt-4">
          <Row gutter={[16, 16]}>
            <Col className="pt-4 pb-4 rounded-b-md -mt-4" span={12}>
              <Collapse
                className="bg-white"
                size="large"
                items={[
                  {
                    key: "2",
                    label: "Status",
                    children: renderContent(SettingMasterdataEnum.FUGITIVE_STATUS),
                  },
                ]}
              />
            </Col>
            <Col className="pt-4 pb-4 rounded-b-md -mt-4" span={12}>
              <Collapse
                className="bg-white"
                size="large"
                items={[
                  {
                    key: "3",
                    label: "Equipment Type",
                    children: renderContent(SettingMasterdataEnum.FUGITIVE_EQUIPMENT_TYPE),
                  },
                ]}
              />
            </Col>
            <Col className="pt-4 pb-4 rounded-b-md -mt-4" span={12}>
              <Collapse
                className="bg-white"
                size="large"
                items={[
                  {
                    key: "3",
                    label: "Uncheck",
                    children: renderContent(SettingMasterdataEnum.FUGITIVE_UNCHECK),
                  },
                ]}
              />
            </Col>
            <Col className="pt-4 pb-4 rounded-b-md -mt-4" span={12}>
              <Collapse
                className="bg-white"
                size="large"
                items={[
                  {
                    key: "3",
                    label: "STD Control",
                    children: renderContent(SettingMasterdataEnum.FUGITIVE_STD_CONTROL),
                  },
                ]}
              />
            </Col>
            <Col className="pt-4 pb-4 rounded-b-md -mt-4" span={14}>
              <Collapse
                className="bg-white"
                size="large"
                items={[
                  {
                    key: "4",
                    label: "Component by series",
                    children: renderContent(SettingMasterdataEnum.FUGITIVE_COMPONENT_BY_SERIES),
                  },
                ]}
              />
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: "5",
      label: "Tank",
      menuValue: SettingMasterdataEnum.TANK,
      children: (
        <div className="bg-white pt-4 pb-4 rounded-b-md -mt-4">
          <Row gutter={[16, 16]}>
            <Col className="pt-4 pb-4 rounded-b-md -mt-4" span={12}>
              <Collapse
                className="bg-white"
                size="large"
                items={[
                  {
                    key: "2",
                    label: "Tank Type",
                    children: renderContent(SettingMasterdataEnum.TANK_TANK_TYPE),
                  },
                ]}
              />
            </Col>
            <Col className="pt-4 pb-4 rounded-b-md -mt-4" span={12}>
              <Collapse
                className="bg-white"
                size="large"
                items={[
                  {
                    key: "3",
                    label: "Emission Control",
                    children: renderContent(SettingMasterdataEnum.TANK_EMISSION_CONTROL),
                  },
                ]}
              />
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: "6",
      label: "Wastewater Treatment",
      menuValue: SettingMasterdataEnum.WASTE_WATER_TREATMENT,
      // children: renderContent(SettingMasterdataEnum.WASTE_WATER_TREATMENT),
      children: (
        <Col span={24} className="bg-white pt-4 pb-4 rounded-b-md -mt-4">
          <Collapse
            className="bg-white"
            size="large"
            items={[{ key: "1", label: "Emission Control", children: renderContent(SettingMasterdataEnum.WASTE_WATER_TREATMENT) }]}
          />
        </Col>
      ),
    },
  ];

  const onTabChange = (val: string) => {
    setActiveMasterdata(val);
  };
  return (
    <Row className="w-full" id={"masterdata-setting"}>
      <ConfigProvider
        theme={{
          components: {
            Tabs: {
              colorBgContainer: componentPalette.yellow,
              itemSelectedColor: componentPalette.black,
              cardBg: componentPalette.white,
            },
          },
        }}
      >
        <Tabs
          defaultActiveKey="1"
          onTabClick={onTabChange}
          type="card"
          size={"middle"}
          items={titleBar}
          className="w-full"
          tabBarGutter={6}
        />
      </ConfigProvider>
    </Row>
  );
};

export default SettingCalculation;
